/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { BuilderPermission, isLendersScheduleCode } from '@gh/builder-shared-data';
import { isJobEditable } from '../../../';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  markFormStateAsTouched,
  mergeFormStateSnapshotValue,
  PlainImmutablePageState,
  queryRecalculationNeeded,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { find, isEmpty, negate, omit } from 'lodash';
import {
  JOB_DRAWS_DETAILS_INIT,
  JobDrawsDetails,
  JobDrawsDetailsInfo,
  JobDrawsDetailsTab,
  JobDrawsRecalculateRequest,
  validateJobDrawsDetailsInfo,
} from '../../resources/job-details-tab';

@Injectable()
export class JobDrawDetailsDrawsFormState extends PlainImmutablePageState<JobDrawsDetails> {
  recalculationNeeded$ = this.select(queryRecalculationNeeded);
  selectedTab$ = this.select(({ selectedTab }) => selectedTab);
  currentRuleId$ = this.select(({ currentRuleId }) => currentRuleId);
  selectedInvoiceId$ = this.select(({ selectedInvoiceId }) => selectedInvoiceId);
  rules$ = this.select(({ rules }) => rules);
  info$ = this.select(({ info }) => info);
  invoices$ = this.select(({ invoices }) => invoices);
  paymentSchedule$ = this.select(({ paymentSchedule }) => paymentSchedule);
  lendersSchedule$ = this.select(({ lendersSchedule }) => lendersSchedule);
  contractAmount$ = this.info$.pipe(map$(getFormStateSnapshotValue),
                                    map$(({ contractAmount }) => contractAmount));
  contractAmountLenders$ = this.info$.pipe(map$(getFormStateSnapshotValue),
                                           map$(({ contractAmountLenders, variationsAmount }) =>
                                                  contractAmountLenders + variationsAmount));
  hasInvoice$ = this.invoices$.pipe(map$(negate(isEmpty)));
  currentRule$ = this.select(({ currentRuleId, rules }) => find(rules, { id: currentRuleId }));
  isLendersScheduleRule$ = this.currentRule$.pipe(map$((rule) => rule ? isLendersScheduleCode(rule.code) : false));

  private originalDetails: JobDrawsDetails;

  constructor(private userContext: UserContext) {
    super(JOB_DRAWS_DETAILS_INIT);
  }

  get isPriceWasModified(): boolean {
    const info = getFormStateSnapshotValue(this.value.info);
    return info.isContractAmountManual || info.isContractAmountWithGstManual;
  }

  get modifiedRuleId(): Maybe<ID> {
    const { currentRuleId, isDrawRuleModified, isLendersScheduleRefreshed } = this.value;
    return isDrawRuleModified || isLendersScheduleRefreshed ? currentRuleId : void 0;
  }

  get hasInvoice(): boolean {
    return !isEmpty(this.value.invoices);
  }

  get currentRuleId(): Maybe<ID> {
    return this.value.currentRuleId;
  }

  get invalid(): boolean {
    return this.isInfoInvalid;
  }

  get isInfoInvalid(): boolean {
    return isNotNil(validateJobDrawsDetailsInfo(getFormStateSnapshotValue(this.value.info)));
  }

  build(details: JobDrawsDetails): void {
    this.originalDetails = details;

    this.setValue(details);
    this.setMode(this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT) && isJobEditable(details)
                   ? FormMode.EditMode
                   : FormMode.ViewMode);
  }

  reset(): void {
    this.setValue(this.originalDetails);
    this.resetDirty();
  }

  mergeOnRecalculate(details: JobDrawsDetails): void {
    // update info form snapshot, but leave manual fields as is
    this.setField('info', mergeFormStateSnapshotValue(
      this.value.info,
      <JobDrawsDetailsInfo>omit(
        getFormStateSnapshotValue(details.info),
        ['isContractAmountManual', 'isContractAmountWithGstManual'])));
    this.setField('invoices', details.invoices);
    this.setField('paymentSchedule', details.paymentSchedule);
    this.setField('lendersSchedule', details.lendersSchedule);
    this.setField('selectedInvoiceId', details.selectedInvoiceId);
    this.setField('isRecalculationNeeded', false);
  }

  setInfo(info: FormStateSnapshot<JobDrawsDetailsInfo>): void {
    this.setFieldWithDirty('info', info);
    this.setRecalculationNeeded();
  }

  setCurrentRuleId(ruleId?: ID): void {
    this.setField('currentRuleId', ruleId);
  }

  finalizeCurrentRuleChange(): void {
    this.setField('isLendersScheduleRefreshed', false);
  }

  setSelectedInvoiceId(invoiceId: ID | undefined): void {
    this.setField('selectedInvoiceId', invoiceId);
  }

  selectTab(tab: JobDrawsDetailsTab): void {
    this.setField('selectedTab', tab);
  }

  setRecalculationNeeded(): void {
    this.setField('isRecalculationNeeded', true);
  }

  markDrawRuleAsModified(): void {
    this.setField('isDrawRuleModified', true);
  }

  markScheduleAsRefreshed(): void {
    this.setField('isLendersScheduleRefreshed', true);
  }

  prepareRecalculateRequest(isSave: boolean, isContractPriceUpdate: boolean = false): JobDrawsRecalculateRequest {
    const info = getFormStateSnapshotValue(this.value.info);

    return {
      drawRuleId: this.value.originalRuleId,
      contractAmount: info.contractAmount || 0,
      contractAmountWithGst: info.contractAmountWithGst || 0,
      isContractAmountManual: info.isContractAmountManual,
      isContractAmountWithGstManual: info.isContractAmountWithGstManual,
      isContractPriceUpdate,
      isSave,
      version: this.value.version,
    };
  }

  highlightMasterInfo(): void {
    this.setField('info', markFormStateAsTouched(this.value.info));
  }
}
